import React, { useState, useEffect, useMemo } from 'react';
import { 
    FileText, RefreshCcw, Archive, Download, Calendar, 
    FileDown, ListFilter, Eye, Lock, Snowflake, Inbox, ChevronDown, ChevronUp, Clock, UserCheck, RefreshCw 
} from 'lucide-react';
import API from '../../services/api';
import Sidebar from '../../components/Sidebar';
import * as XLSX from 'xlsx';

const HistoriquePSF = () => {
    // --- ÉTATS ---
    const [productions, setProductions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showFullHistory, setShowFullHistory] = useState(false);
    const [expandedRow, setExpandedRow] = useState(null); 
    
    const [isInventoryActive, setIsInventoryActive] = useState(false);
    const [lastClosureDate, setLastClosureDate] = useState(null);
    const [activeTab, setActiveTab] = useState('actif');

    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    // Filtres de colonnes pour le tableau détaillé
    const [colFilters, setColFilters] = useState({ id: '', code_groupe: '', article_nom: '' });

    // --- STYLE BORDEAUX ---
    const bordeaux = '#800020';
    const bordeauxLight = '#80002015'; 

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await API.get('/production-needs/historique'); 
            setProductions(Array.isArray(res.data) ? res.data : []);
            
            const resInv = await API.get('/inventories/check-status'); 
            setIsInventoryActive(!!resInv.data.en_cours); 
            setLastClosureDate(resInv.data.last_closure || null);
        } catch (err) { 
            console.error("Erreur chargement PSF:", err); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { fetchData(); }, []);

    const isRowFrozen = (date) => lastClosureDate && new Date(date) <= new Date(lastClosureDate);

    // Filtrage par onglet (Actifs vs Archives)
    const dataParOnglet = useMemo(() => {
        return productions.filter(p => {
            const frozen = isRowFrozen(p.date_stockage);
            return activeTab === 'archives' ? frozen : !frozen;
        });
    }, [productions, activeTab, lastClosureDate]);

    // Filtrage dynamique (Dates + Recherche colonnes)
    const filteredData = useMemo(() => {
        return dataParOnglet.filter(p => {
            const matchesDate = (!dateRange.start || p.date_stockage >= dateRange.start) && 
                              (!dateRange.end || p.date_stockage <= dateRange.end);
            
            const matchesId = String(p.id).toLowerCase().includes(colFilters.id.toLowerCase());
            const matchesArticle = (p.article_nom || '').toLowerCase().includes(colFilters.article_nom.toLowerCase());
            const matchesSession = (p.code_groupe || '').toLowerCase().includes(colFilters.code_groupe.toLowerCase());
            
            return matchesDate && matchesId && matchesArticle && matchesSession;
        });
    }, [dataParOnglet, dateRange, colFilters]);

    // Groupement par Session
    const groupedBySession = useMemo(() => {
        const groups = {};
        dataParOnglet.forEach(p => {
            const sessionKey = p.code_groupe || "SANS-SESSION";
            if (!groups[sessionKey]) {
                groups[sessionKey] = { session_id: sessionKey, date: p.date_stockage, total_valeur: 0, nb: 0 };
            }
            groups[sessionKey].total_valeur += Number(p.quantite_recue || 0) * Number(p.cout_unitaire_reel || 0);
            groups[sessionKey].nb++;
        });
        return Object.values(groups);
    }, [dataParOnglet]);

    const toggleRow = (id) => setExpandedRow(expandedRow === id ? null : id);

    // Formater date et heure
    const formatFullDate = (dateStr) => {
        if (!dateStr) return "-";
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div style={layoutStyle}>
            <Sidebar />
            <main style={mainStyle}>
                <header style={{...headerBarStyle, borderBottom: `3px solid ${bordeaux}`}}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{...iconBox, background: bordeaux}}><FileText size={24} color="#fff" /></div>
                        <div>
                            <h1 style={titleStyle}>HISTORIQUE ANALYTIQUE DES PRODUCTIONS (PSF)</h1>
                            <div style={dateBox}>
                                <Calendar size={14} color={bordeaux} />
                                <input type="date" style={dateInput} value={dateRange.start} onChange={(e) => setDateRange({...dateRange, start: e.target.value})} />
                                <span style={{fontWeight: '800'}}>au</span>
                                <input type="date" style={dateInput} value={dateRange.end} onChange={(e) => setDateRange({...dateRange, end: e.target.value})} />
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={() => {const ws=XLSX.utils.json_to_sheet(filteredData); const wb=XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb,ws,"PSF"); XLSX.writeFile(wb,`Histo_PSF.xlsx`)}} style={btnExcel}><Download size={16} /> Excel</button>
                        <button onClick={fetchData} style={{...btnRefresh, borderColor: bordeaux}}><RefreshCw size={16} color={bordeaux} className={loading ? 'animate-spin' : ''} /></button>
                    </div>
                </header>

                <div style={contentArea}>
                    <div style={tabHeader}>
                        <div style={{...tabContainer, border: `2px solid ${bordeaux}`}}>
                            <button onClick={() => setActiveTab('actif')} style={activeTab === 'actif' ? {...activeTabStyle, color: bordeaux} : inactiveTabStyle}><Inbox size={16}/> ACTIFS</button>
                            <button onClick={() => setActiveTab('archives')} style={activeTab === 'archives' ? {...activeTabStyle, color: '#3b82f6'} : inactiveTabStyle}><Archive size={16}/> ARCHIVES</button>
                        </div>
                        <div style={{display:'flex', gap:'10px'}}>
                            <button onClick={() => { setShowFullHistory(true); setColFilters({id:'', code_groupe:'', article_nom:''}); }} style={{...btnShowAll, background: bordeaux}}><Eye size={14} /> DÉTAILS COMPLETS</button>
                            <button onClick={() => {setColFilters({id:'',code_groupe:'',article_nom:''}); setShowFullHistory(false); setDateRange({start:'', end:''})}} style={{...btnReset, border: `2px solid ${bordeaux}`}}><ListFilter size={14} /> RESET FILTRES</button>
                        </div>
                    </div>

                    <h3 style={{...sectionTitle, color: bordeaux}}>RÉSUMÉ DES SESSIONS DE PRODUCTION</h3>
                    <div style={{...cardStyle, border: `2px solid ${bordeaux}30`, maxHeight: '200px', overflowY: 'auto', marginBottom: '20px'}}>
                        <table style={mainTable}>
                            <thead style={stickyHeader}>
                                <tr style={{background: bordeauxLight, color: bordeaux}}>
                                    <th style={thStyle}>CODE SESSION</th>
                                    <th style={thCenter}>NB ARTICLES</th>
                                    <th style={thCenter}>VALEUR TOTALE PROD.</th>
                                    <th style={thStyle}>DERNIER STOCKAGE</th>
                                    <th style={thCenter}>ÉTAT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedBySession.map(session => (
                                    <tr key={session.session_id} style={trStyle}>
                                        <td style={tdStyle}>
                                            <span style={{...lotBadge, background: bordeauxLight, color: bordeaux, border: `1px solid ${bordeaux}`}} 
                                                  onClick={() => {setShowFullHistory(true); setColFilters({...colFilters, code_groupe: session.session_id})}}>
                                                {session.session_id}
                                            </span>
                                        </td>
                                        <td style={tdCenter}>{session.nb}</td>
                                        <td style={{...tdCenter, fontWeight:'900', color: bordeaux}}>{Math.round(session.total_valeur).toLocaleString()} F</td>
                                        <td style={tdStyle}>{formatFullDate(session.date)}</td>
                                        <td style={tdCenter}>{isRowFrozen(session.date) ? <Snowflake size={14} color="#3b82f6"/> : <span style={{color:'#10b981'}}>●</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <h3 style={{...sectionTitle, color: bordeaux}}>TRAÇABILITÉ ANALYTIQUE DÉTAILLÉE</h3>
                    <div style={{...cardStyle, border: `2px solid ${bordeaux}30`, flex: 1, overflowY: 'auto'}}>
                        <table style={mainTable}>
                            <thead style={stickyHeader}>
                                <tr style={{background: bordeauxLight, color: bordeaux}}>
                                    <th style={{...thStyle, width: '40px'}}></th>
                                    <th style={thStyle}>
                                        ID / ARTICLE / SESSION
                                        <div style={{marginTop:'5px', display:'flex', gap:'5px'}}>
                                            <input placeholder="ID..." style={headerSearch} value={colFilters.id} onChange={e => setColFilters({...colFilters, id: e.target.value})} />
                                            <input placeholder="Article..." style={headerSearch} value={colFilters.article_nom} onChange={e => setColFilters({...colFilters, article_nom: e.target.value})} />
                                        </div>
                                    </th>
                                    <th style={thCenter}>PRÉVU</th>
                                    <th style={thCenter}>PRODUIT</th>
                                    <th style={thCenter}>EN STOCK</th>
                                    <th style={thCenter}>COÛT RÉEL (CMP)</th>
                                    <th style={thCenter}>VALEUR TOTALE</th>
                                    <th style={thStyle}>STOCKÉ LE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {showFullHistory ? filteredData.map(p => (
                                    <React.Fragment key={p.id}>
                                        <tr style={trStyle}>
                                            <td style={tdCenter}><button onClick={() => toggleRow(p.id)} style={btnExpand}>{expandedRow === p.id ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}</button></td>
                                            <td style={tdStyle}>
                                                <div style={{fontWeight:'900', color: bordeaux}}>{p.id}</div>
                                                <div style={{fontWeight:'700'}}>{p.article_nom}</div>
                                                <div style={{fontSize:'10px', color: '#64748b'}}>Session: {p.code_groupe}</div>
                                            </td>
                                            <td style={tdCenter}>{parseFloat(p.quantite).toFixed(3)}</td>
                                            <td style={tdCenter}>{parseFloat(p.quantite_produite).toFixed(3)}</td>
                                            <td style={{...tdCenter, fontWeight:'900', color:'#16a34a'}}>{parseFloat(p.quantite_recue).toFixed(3)}</td>
                                            <td style={{...tdCenter, color:'#b45309'}}>{parseFloat(p.cout_unitaire_reel || 0).toLocaleString()} F</td>
                                            <td style={{...tdCenter, fontWeight:'900', color: bordeaux}}>{Math.round(p.quantite_recue * p.cout_unitaire_reel).toLocaleString()} F</td>
                                            <td style={tdStyle}>{formatFullDate(p.date_stockage)}</td>
                                        </tr>
                                        {expandedRow === p.id && (
                                            <tr>
                                                <td colSpan="8" style={expandedArea}>
                                                    <div style={traceGrid}>
                                                        <div style={{...traceCard, borderLeft: `4px solid ${bordeaux}`}}><div style={traceTitle}><Clock size={12}/> DEMANDE</div><b>{formatFullDate(p.date_emission)}</b><br/><span><UserCheck size={11}/> {p.emetteur_nom || 'Demandeur'}</span></div>
                                                        <div style={{...traceCard, borderLeft: `4px solid ${bordeaux}`}}><div style={traceTitle}><Clock size={12}/> VALIDATION</div><b>{formatFullDate(p.date_validation_magasin || p.date_emission)}</b><br/><span><UserCheck size={11}/> {p.valideur_magasin_nom || 'Magasinier'}</span></div>
                                                        <div style={{...traceCard, borderLeft: `4px solid ${bordeaux}`}}><div style={traceTitle}><Clock size={12}/> PRODUCTION</div><b>{formatFullDate(p.date_production_fin || p.date_stockage)}</b><br/><span><UserCheck size={11}/> {p.chef_production_nom || 'Chef Prod.'}</span></div>
                                                        <div style={{...traceCard, borderLeft: `4px solid ${bordeaux}`}}><div style={traceTitle}><Clock size={12}/> MISE EN STOCK</div><b>{formatFullDate(p.date_stockage)}</b><br/><span><UserCheck size={11}/> {p.receptionnaire_nom || 'Magasinier'}</span></div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )) : <tr><td colSpan="8" style={emptyState}>Sélectionnez une session ou cliquez sur "Détails complets"</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
};

// --- STYLES ---
const layoutStyle = { display: 'flex', height: '100vh', background: '#F1F5F9' };
const mainStyle = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };
const headerBarStyle = { background: '#fff', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const titleStyle = { margin: 0, fontSize: '18px', fontWeight: '900', color: '#000' };
const iconBox = { padding: '8px', borderRadius: '8px' };
const dateBox = { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px', fontSize: '12px' };
const dateInput = { border: '1px solid #cbd5e1', borderRadius: '4px', padding: '2px 5px', fontWeight: 'bold' };
const contentArea = { padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', flex: 1 };
const sectionTitle = { fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '8px' };
const cardStyle = { background: '#fff', borderRadius: '10px', overflow: 'hidden' };
const mainTable = { width: '100%', borderCollapse: 'collapse', minWidth: '1000px' };
const thStyle = { padding: '12px 10px', fontSize: '11px', fontWeight: '900', textAlign: 'left' };
const thCenter = { ...thStyle, textAlign: 'center' };
const tdStyle = { padding: '10px', fontSize: '11px', color: '#000', borderBottom: '1px solid #f1f5f9' };
const tdCenter = { ...tdStyle, textAlign: 'center' };
const trStyle = { borderBottom: '1px solid #cbd5e1' };
const lotBadge = { padding: '3px 8px', borderRadius: '5px', fontSize: '10px', fontWeight: '900', cursor: 'pointer' };
const tabHeader = { display: 'flex', justifyContent: 'space-between', marginBottom: '15px' };
const tabContainer = { display: 'flex', background: '#e2e8f0', padding: '3px', borderRadius: '8px' };
const inactiveTabStyle = { padding: '6px 15px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '11px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' };
const activeTabStyle = { ...inactiveTabStyle, background: '#fff', borderRadius: '5px', fontWeight: '900' };
const btnExcel = { background: '#059669', color: '#fff', border: 'none', padding: '8px 15px', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', display: 'flex', gap: '8px', fontSize: '11px' };
const btnRefresh = { background: '#fff', padding: '8px', borderRadius: '8px', cursor: 'pointer', border: '2px solid' };
const stickyHeader = { position: 'sticky', top: 0, zIndex: 10 };
const emptyState = { textAlign: 'center', padding: '40px', color: '#64748b', fontStyle: 'italic', fontSize: '13px' };
const btnShowAll = { color: '#fff', border: 'none', padding: '8px 15px', borderRadius: '6px', fontSize: '11px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' };
const btnReset = { background: '#fff', color: '#000', padding: '8px 14px', borderRadius: '6px', fontSize: '11px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' };
const btnExpand = { background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const expandedArea = { background: '#f8fafc', padding: '15px', borderBottom: '2px solid #cbd5e1' };
const traceGrid = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' };
const traceCard = { background: '#fff', padding: '10px', borderRadius: '8px', fontSize: '11px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' };
const traceTitle = { color: '#64748b', fontWeight: '900', fontSize: '9px', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px' };

const headerSearch = { 
    padding: '4px 8px', 
    borderRadius: '4px', 
    border: '1px solid #cbd5e1', 
    fontSize: '10px', 
    width: '100px', 
    fontWeight: 'normal',
    color: '#000'
};

export default HistoriquePSF;